import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Navigation, Pagination } from "swiper";
import Particle from "./Particle";
import { ChevronDown } from "lucide-react";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";

const heroSlides = [
  {
    image:
      "/Images/bg-2.jpg",
    fallbackImage: "/Images/RIVERA_THE_VALLEY_IMAGE02.jpg",
    title: "Inspired By Nature, Designed for Life",
    subtitle:
      "Experience the epitome of luxury in Dubai's most prestigious locations",
    cta: "Explore Properties",
    particles: ["🏙️", "🌴", "✨", "🏊", "🚤"],
  },
  {
    image:
    "/Images/bg-3.jpg",
    fallbackImage: "/Images/RIVERA_THE_VALLEY_IMAGE05.jpg",
    title: "Emaar Rivera, Nature's Finest Address",
    subtitle: "Discover breathtaking views and exclusive lifestyle",
    cta: "View Waterfront Homes",
    particles: ["🌊", "⛵", "🏝️", "🌅", "🏄"],
  },
  {
    image:
    "/Images/bg-1.jpg",
    fallbackImage: "/Images/RIVERA_THE_VALLEY_IMAGE07.jpg",
    title: "Green living Elevated",
    subtitle:
      "Live in meticulously planned neighborhoods with premium amenities",
    cta: "Explore Communities",
    particles: ["🏘️", "🏟️", "🌳", "🚶", "🚴"],
  },
];

const Hero = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [slideImages, setSlideImages] = useState(
    heroSlides.map((slide) => ({ src: slide.image, useFallback: false }))
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleImageError = (index) => {
    setSlideImages((prevImages) => {
      const newImages = [...prevImages];
      newImages[index] = {
        ...newImages[index],
        src: heroSlides[index].fallbackImage,
        useFallback: true,
      };
      return newImages;
    });
  };

  return (
    <section className="relative h-screen w-full overflow-hidden" id="home">
      {/* Animated background overlay */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-black/70"
          animate={{
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "linear",
          }}
        />
      </div>

      {/* Floating particles background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {isMounted &&
          heroSlides[activeSlide].particles.map((emoji, i) => (
            <Particle
              key={i}
              emoji={emoji}
              x={Math.random() * 100}
              y={Math.random() * 100}
              delay={Math.random() * 2}
              duration={5 + Math.random() * 5}
            />
          ))}
      </div>

      {/* Gold dust particles effect */}
      <div className="absolute inset-0 z-1 overflow-hidden">
        {Array.from({ length: 30 }).map((_, i) => (
          <motion.div
            key={`gold-dust-${i}`}
            className="absolute rounded-full bg-gradient-to-br from-[#d2ac47] to-[#b08e22]"
            style={{
              width: `${Math.random() * 8 + 3}px`,
              height: `${Math.random() * 8 + 3}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.3 + Math.random() * 0.5,
            }}
            animate={{
              y: [0, Math.random() * 100 - 50],
              x: [0, Math.random() * 100 - 50],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: Math.random() * 15 + 10,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "linear",
            }}
          />
        ))}
      </div>

      {/* Shimmering light effect */}
      <div className="absolute inset-0 z-2 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute w-[300px] h-[300px] rounded-full bg-[#d2ac47]/10 blur-[100px]"
          animate={{
            x: [0, 100, -100, 0],
            y: [0, 100, -100, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      {/* Hero Slider */}
      <Swiper
        modules={[Autoplay, EffectFade, Navigation, Pagination]}
        effect="fade"
        navigation={{
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        }}
        pagination={{
          clickable: true,
          el: ".swiper-pagination",
          renderBullet: (index, className) => {
            return `<span class="${className} bg-gradient-to-b from-[#d2ac47] to-[#b08e22] opacity-70 hover:opacity-100 transition-all duration-300 w-3 h-3 mx-1 rounded-full"></span>`;
          },
        }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        speed={1500}
        loop={true}
        onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
        className="h-full w-full"
      >
        {heroSlides.map((slide, index) => (
          <SwiperSlide key={index} className="h-full w-full">
            <div
              className="relative h-full w-full overflow-hidden"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {/* Dark overlay gradient that covers the entire slide */}
              <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/40 via-black/20 to-black/40" />

              {/* Image with error handling */}
              <motion.img
                src={slideImages[index].src}
                alt={slide.title}
                onError={() => handleImageError(index)}
                className="absolute inset-0 h-full w-full object-cover"
                style={{ objectPosition: "center center" }}
                initial={{ scale: 1 }}
                animate={{
                  scale: isHovered ? 1.05 : 1,
                }}
                transition={{
                  scale: {
                    duration: 8,
                    ease: "easeInOut",
                  },
                }}
                loading="eager"
              />

              {/* Gold shimmer effect */}
              <div className="absolute inset-0 overflow-hidden z-5">
                <motion.div
                  className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-[#d2ac47]/20 to-transparent"
                  initial={{ x: "-100%", rotate: -15 }}
                  animate={{
                    x: "100%",
                    transition: {
                      duration: 12,
                      repeat: Infinity,
                      ease: "linear",
                    },
                  }}
                />
              </div>

              {/* Subtle vignette effect */}
              <div className="absolute inset-0 z-10 shadow-[inset_0_0_150px_rgba(0,0,0,0.7)]" />

              <div className="absolute inset-0 z-20 flex items-center justify-center px-4 sm:px-6 lg:px-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`slide-${index}`}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{
                      opacity: activeSlide === index ? 1 : 0,
                      y: activeSlide === index ? 0 : 40,
                    }}
                    exit={{ opacity: 0, y: -40 }}
                    transition={{
                      duration: 0.8,
                      ease: [0.6, -0.05, 0.01, 0.99],
                    }}
                    className="text-center text-white max-w-4xl mx-auto"
                  >
                    {/* Luxury decorative element */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 }}
                      className="flex justify-center mb-6"
                    >
                      <div className="relative">
                        <div className="h-1 w-24 bg-gradient-to-r from-[#d2ac47] to-[#b08e22] mr-2 rounded-full"></div>
                        <div className="absolute -bottom-1 left-0 right-0 h-1 bg-[#d2ac47]/30 blur-sm"></div>
                      </div>
                      <div className="mx-4 flex items-center">
                        {[...Array(3)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="w-2 h-2 rounded-full bg-gradient-to-br from-[#d2ac47] to-[#b08e22] mx-1 shadow-[0_0_8px_rgba(210,172,71,0.8)]"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2 + i * 0.1 }}
                          />
                        ))}
                      </div>
                      <div className="relative">
                        <div className="h-1 w-24 bg-gradient-to-r from-[#b08e22] to-[#d2ac47] ml-2 rounded-full"></div>
                        <div className="absolute -bottom-1 left-0 right-0 h-1 bg-[#d2ac47]/30 blur-sm"></div>
                      </div>
                    </motion.div>

                    <motion.h1
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-center text-4xl sm:text-5xl md:text-6xl font-bold mb-6"
                      style={{ fontFamily: '"Playfair Display", serif' }}
                    >
                      {slide.title.split(" ").map((word, i) => (
                        <motion.span
                          key={i}
                          className="mr-2 bg-gradient-to-r from-[#d2ac47] via-white to-[#d1d5db] bg-clip-text text-transparent drop-shadow-lg hover:drop-shadow-2xl transition duration-300"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 + i * 0.1 }}
                          whileHover={{
                            scale: 1.05,
                            textShadow: "0 0 15px rgba(210, 172, 71, 0.8)",
                          }}
                        >
                          {word}
                        </motion.span>
                      ))}
                    </motion.h1>

                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 }}
                      className="mb-10 max-w-2xl mx-auto relative"
                    >
                      <div className="absolute -left-8 top-1/2 transform -translate-y-1/2 w-16 h-0.5 bg-gradient-to-r from-transparent to-[#d2ac47] rounded-full"></div>
                      {/* <motion.p
                        className="text-xl sm:text-2xl md:text-3xl font-medium text-[#e5e7eb] drop-shadow-md"
                        whileHover={{
                          textShadow: "0 0 10px rgba(255,255,255,0.5)",
                        }}
                      >
                        {slide.subtitle}
                      </motion.p> */}
                      <div className="absolute -right-8 top-1/2 transform -translate-y-1/2 w-16 h-0.5 bg-gradient-to-r from-[#d2ac47] to-transparent rounded-full"></div>
                    </motion.div>

                    {/* <motion.button
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 }}
                      whileHover={{
                        scale: 1.05,
                        boxShadow: "0 0 30px rgba(210, 172, 71, 0.7)",
                      }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-[#c6a136] to-[#d2ac47] text-white px-10 py-4 rounded-full text-lg font-medium transition-all duration-300 relative overflow-hidden group"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        {slide.cta}
                        <motion.span
                          className="ml-2"
                          animate={{
                            x: [0, 5, 0],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                          }}
                        >
                          →
                        </motion.span>
                      </span>
                      <span className="absolute inset-0 bg-gradient-to-r from-[#b08e22] to-[#c6a136] opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0"></span>
                      <motion.span
                        className="absolute top-0 left-0 w-full h-0.5 bg-white/50"
                        initial={{ x: "-100%" }}
                        whileHover={{
                          x: "100%",
                          transition: { duration: 0.8 },
                        }}
                      />
                      
                      <motion.div
                        className="absolute top-0 left-0 w-10 h-full bg-white/30 skew-x-[-20deg]"
                        initial={{ x: "-100%" }}
                        whileHover={{
                          x: "300%",
                          transition: { duration: 0.8 },
                        }}
                      />
                    </motion.button> */}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom Pagination with progress indicator */}
      <div className="absolute bottom-8 left-0 right-0 z-30">
        <div className="swiper-pagination !bottom-0 flex justify-center items-center"></div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-30 text-white flex flex-col items-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
      >
        <span className="text-sm mb-2 font-medium text-[#ffd166]">
          Scroll Down
        </span>
        <motion.div
          animate={{
            y: [0, 10, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <ChevronDown className="h-6 w-6 text-[#d2ac47]" />
        </motion.div>
      </motion.div>

      {/* Property stats */}
      {/* <motion.div
        className="absolute right-8 bottom-8 z-30 hidden md:flex space-x-8"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.3 }}
      >
        <PropertyStat number="500+" label="Properties" />
        <PropertyStat number="20+" label="Years" />
        <PropertyStat number="100%" label="Satisfaction" />
      </motion.div> */}
    </section>
  );
};

const PropertyStat = ({ number, label }) => (
  <motion.div
    className="text-center glass-gold p-4 rounded-lg backdrop-blur-sm"
    whileHover={{
      scale: 1.05,
      boxShadow: "0 0 20px rgba(210, 172, 71, 0.5)",
    }}
  >
    <p className="text-[#d2ac47] font-bold text-3xl mb-1">{number}</p>
    <p className="text-[#d1d5db] text-xs uppercase tracking-widest">{label}</p>
    <motion.div
      className="absolute inset-0 bg-white/10 rounded-lg pointer-events-none"
      initial={{ x: "-100%" }}
      animate={{
        x: "100%",
        transition: {
          duration: 6,
          repeat: Infinity,
          repeatDelay: 2,
          ease: "easeInOut",
        },
      }}
    />
  </motion.div>
);

export default Hero;


